import type { FC } from "react";
import type { TimelineItemResponseDTO as TimelineItem } from "web_myplann/Infrastructure/Api/Api";
import { TimelineItemLayout } from "web_myplann/NotificationCenter/Components/TimelineItemLayout";
import {
	NOTIFICATION_CENTER_TABS,
	type NotificationCenterTab,
} from "web_myplann/NotificationCenter/Constants/notificationCenterTabs";
import { useMarkNotificationAsRead } from "web_myplann/NotificationCenter/Hooks/useMarkNotificationAsRead";
import {
	formatTimelineDate,
	formatTimelineTime,
} from "web_myplann/NotificationCenter/Utils/formatTimelineDate";
import { isUnreadNotification } from "web_myplann/NotificationCenter/Utils/isUnreadNotification";
import { resolveTimelineNavigation } from "web_myplann/NotificationCenter/Utils/resolveTimelineNavigation";

interface TimelineEntryProps {
	item: TimelineItem;
	tab: NotificationCenterTab;
	onClick?: (item: TimelineItem) => void;
	isLast?: boolean;
}

// Pre-existing behaviour: an item that carries a time shows only that time,
// leaning on the surrounding bucket heading for the day.
const formatDateLine = (item: TimelineItem): string =>
	item.time ? formatTimelineTime(item.time) : formatTimelineDate(item.date);

export const TimelineEntry: FC<TimelineEntryProps> = ({
	item,
	tab,
	onClick,
	isLast = false,
}) => {
	const markAsRead = useMarkNotificationAsRead();
	const unread = isUnreadNotification(item);
	const isNavigable = resolveTimelineNavigation(item) !== null;
	const showUnread = tab === NOTIFICATION_CENTER_TABS.Recent && unread;

	const handleClick = () => {
		if (unread) {
			markAsRead(item);
		}
		if (isNavigable) {
			onClick?.(item);
		}
	};

	return (
		<TimelineItemLayout
			item={item}
			dateLine={formatDateLine(item)}
			unread={showUnread}
			showChevron={isNavigable}
			disabled={!isNavigable && !unread}
			onClick={handleClick}
			isLast={isLast}
			data-testid={`timeline-entry-${item.id}`}
		/>
	);
};
